var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useState, forwardRef, useImperativeHandle, } from "react";
import { Box, Grid, Typography, Alert, useMediaQuery } from "@mui/material";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
// Components
import { FSwitch } from "@ui/components/inputs/FSwitch";
import { FSelect } from "@ui/components/inputs/FSelect";
import { FInput } from "@ui/components/inputs/FInput";
import { FPhoneInput } from "@ui/components/inputs/FPhoneInput";
import { FTimePicker } from "@ui/components/inputs/FTimePicker";
import { FMultiSelectChips } from "@ui/components/inputs/FMultiSelectChips";
import { SButton } from "@ui/components/buttons/SButton";
import { SIconButton } from "@ui/components/buttons";
import { SectionWithInfo } from "@ui/components/section/SectionWithInfo";
// Redux
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { updateNode } from "@reducers/nodes/thunk/nodes-thunk";
import { toast } from "@ui/utils/toaster";
import { useTranslation } from "react-i18next";
import { tooltipTextsApi, TOOLTIP_KEYS } from "@api/enpoints/tooltip-texts-api";
import { nodeAlertTypesToBooleans, booleansToNodeAlertTypes, } from "../../utils/node-alarm-transform";
import { colCenter, jEnd } from "@ui/components/style/style";
import { AlertType, RepeatAlarm, } from "@reducers/nodes/models/nodes-dto";
import { alertTypeToSelectValue, parseAlertTypeOrDefault, parseRepeatAlarmOrDefault, repeatAlarmToSelectValue, } from "@utility/alarm-enum-coercion";
import { useAlarmRecipientCrossValidation } from "@ui/hooks/useAlarmRecipientCrossValidation";
// Capture the browser's IANA timezone once at module load time.
// This is used when saving new schedules so the backend can evaluate
// blocking windows in the user's actual local time regardless of server TZ.
const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const defaultContact = () => ({
    email: "",
    number: "",
    alertReportTypes: [],
    startTime: null,
    endTime: null,
    timeZoneId: BROWSER_TIMEZONE,
});
// Helper to parse time string "HH:mm:ss" or "HH:mm" to Date
const parseTimeString = (timeStr) => {
    if (!timeStr)
        return null;
    try {
        const today = new Date();
        const [hours, minutes, seconds] = timeStr.split(":").map(Number);
        today.setHours(hours || 0, minutes || 0, seconds || 0, 0);
        return today;
    }
    catch (_a) {
        return null;
    }
};
// Helper to format Date to time string "HH:mm:ss"
const formatTimeToString = (date) => {
    if (!date)
        return undefined;
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
};
function contactHasEmailOrPhone(c) {
    var _a, _b;
    const email = (_a = c.email) === null || _a === void 0 ? void 0 : _a.trim();
    const raw = (_b = c.number) === null || _b === void 0 ? void 0 : _b.trim();
    const phoneOk = !!raw && raw !== "+31 ";
    return !!(email || phoneOk);
}
// Transform contact rows to API recipients (single list; SMS derived server-side from phone + times).
function transformContactsForApi(contacts) {
    const filled = contacts.filter(contactHasEmailOrPhone);
    const recipients = filled.map((c) => {
        var _a, _b, _c, _d;
        const flags = nodeAlertTypesToBooleans(c.alertReportTypes);
        const phone = (_a = c.number) === null || _a === void 0 ? void 0 : _a.trim();
        return {
            email: ((_b = c.email) === null || _b === void 0 ? void 0 : _b.trim()) || null,
            phone: !phone || phone === "+31 " ? null : phone.replace(/\s/g, ""),
            startTime: (_c = formatTimeToString(c.startTime)) !== null && _c !== void 0 ? _c : null,
            endTime: (_d = formatTimeToString(c.endTime)) !== null && _d !== void 0 ? _d : null,
            timeZoneId: c.timeZoneId || BROWSER_TIMEZONE,
            allowHighTemp: flags.allowHighTemp,
            allowLowTemp: flags.allowLowTemp,
        };
    });
    return { recipients };
}
// Load contacts from node alarm settings (recipients from API, or legacy shape projected by backend).
function loadContactsFromNode(node) {
    var _a;
    const rec = (_a = node.alarmSettings) === null || _a === void 0 ? void 0 : _a.recipients;
    if (rec && rec.length > 0) {
        return rec.map((r) => {
            var _a, _b, _c;
            return ({
                email: (_b = (_a = r.email) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : "",
                number: ((_c = r.phone) !== null && _c !== void 0 ? _c : "").trim(),
                alertReportTypes: booleansToNodeAlertTypes({
                    allowHighTemp: !!r.allowHighTemp,
                    allowLowTemp: !!r.allowLowTemp,
                }),
                startTime: parseTimeString(typeof r.startTime === "string" ? r.startTime : undefined),
                endTime: parseTimeString(typeof r.endTime === "string" ? r.endTime : undefined),
                // Preserve stored timezone; legacy records without it fall back to browser TZ.
                timeZoneId: r.timeZoneId || BROWSER_TIMEZONE,
            });
        });
    }
    return [defaultContact()];
}
const AlarmSettings = forwardRef(({ isEdit, node, onRecipientSaveBlockedChange }, ref) => {
    var _a;
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation(["common"]);
    const { updateLoading } = useAppSelector((state) => state.nodes);
    const dayOptions = [
        { label: t("common:options.days.monday"), value: "1" },
        { label: t("common:options.days.tuesday"), value: "2" },
        { label: t("common:options.days.wednesday"), value: "3" },
        { label: t("common:options.days.thursday"), value: "4" },
        { label: t("common:options.days.friday"), value: "5" },
        { label: t("common:options.days.saturday"), value: "6" },
        { label: t("common:options.days.sunday"), value: "0" },
    ];
    const alertingOptions = [
        { label: t("common:options.alerting.emailSms"), value: String(AlertType.EmailAndSMS) },
        { label: t("common:options.alerting.emailOnly"), value: String(AlertType.EmailOnly) },
        { label: t("common:options.alerting.smsOnly"), value: String(AlertType.SMSOnly) },
    ];
    const repeatAlarmOptions = [
        { label: t("common:options.repeatAlarm.repeatUntilResolved"), value: String(RepeatAlarm.AlarmRepeatUntilResolved) },
        { label: t("common:options.repeatAlarm.repeat3Times"), value: String(RepeatAlarm.AlaramRepeats3Times) },
        { label: t("common:options.repeatAlarm.noRepeat"), value: String(RepeatAlarm.NoRepeat) },
    ];
    const nodeAlertReportOptions = [
        { label: t("common:options.alertReport.lowTemperature"), value: "lowTemperature" },
        { label: t("common:options.alertReport.highTemperature"), value: "highTemperature" },
    ];
    const [justSaved, setJustSaved] = useState(false);
    const [initializedForNodeId, setInitializedForNodeId] = useState(null);
    // Tooltip state
    const [generalTooltip, setGeneralTooltip] = useState("");
    const [alarmsTooltip, setAlarmsTooltip] = useState("");
    useEffect(() => {
        const fetchTooltips = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b;
            try {
                const res = yield tooltipTextsApi.getTooltipTexts();
                if (((_a = res.data) === null || _a === void 0 ? void 0 : _a.isSuccess) && ((_b = res.data) === null || _b === void 0 ? void 0 : _b.data)) {
                    const d = res.data.data;
                    if (d[TOOLTIP_KEYS.alerting] != null)
                        setGeneralTooltip(d[TOOLTIP_KEYS.alerting]);
                    if (d[TOOLTIP_KEYS.alarmSms] != null)
                        setAlarmsTooltip(d[TOOLTIP_KEYS.alarmSms]);
                }
            }
            catch (_c) {
                // Non-blocking
            }
        });
        fetchTooltips();
    }, []);
    const handleTooltipSave = (key, value) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        try {
            const res = yield tooltipTextsApi.saveTooltipText({ key, text: value });
            if ((_a = res.data) === null || _a === void 0 ? void 0 : _a.isSuccess) {
                toast.success(t("common:toasts.saved"));
            }
            else {
                toast.error(t("common:toasts.failedToSaveSettings"));
            }
        }
        catch (err) {
            toast.error(t("common:toasts.failedToSaveSettings"));
        }
    });
    const { control: alarmControl, handleSubmit, reset, formState: { errors: alarmErrors }, } = useForm({
        defaultValues: {
            blockAlarmEmails: false,
            alerting: String(AlertType.EmailAndSMS),
            repeatAlarm: String(RepeatAlarm.NoRepeat),
            contacts: [defaultContact()],
            blockingSchedules: [],
        },
    });
    const watchedContacts = useWatch({
        control: alarmControl,
        name: "contacts",
    });
    useAlarmRecipientCrossValidation({
        target: "node",
        nodeUid: (_a = node === null || node === void 0 ? void 0 : node.nodeId) !== null && _a !== void 0 ? _a : null,
        contacts: watchedContacts,
        onBlockedChange: onRecipientSaveBlockedChange,
    });
    // Expose submitForm method to parent via ref
    useImperativeHandle(ref, () => ({
        submitForm: () => {
            handleSubmit(handleSaveAlarmSettings)();
        },
    }));
    // Field arrays
    const { fields: contactFields, append: appendContact, remove: removeContact, } = useFieldArray({ control: alarmControl, name: "contacts" });
    const { fields: scheduleFields, append: appendSchedule, remove: removeSchedule, } = useFieldArray({ control: alarmControl, name: "blockingSchedules" });
    // Reset form when node data changes
    useEffect(() => {
        var _a;
        if (justSaved) {
            setJustSaved(false);
            return;
        }
        if (node && (!initializedForNodeId || node.id !== initializedForNodeId)) {
            const alarmSettings = node.alarmSettings;
            const loadedContacts = loadContactsFromNode(node);
            reset({
                blockAlarmEmails: (alarmSettings === null || alarmSettings === void 0 ? void 0 : alarmSettings.blockAllAlaramsAndSMS) || false,
                alerting: alertTypeToSelectValue(alarmSettings === null || alarmSettings === void 0 ? void 0 : alarmSettings.alertType),
                repeatAlarm: repeatAlarmToSelectValue(alarmSettings === null || alarmSettings === void 0 ? void 0 : alarmSettings.alarmRepetition),
                // reportingInterval: alarmSettings?.reportingInterval?.trim() || "day",
                // reportingBlockHours:
                //   alarmSettings?.reportingBlockHours != null
                //     ? String(alarmSettings.reportingBlockHours)
                //     : "24",
                contacts: loadedContacts,
                blockingSchedules: ((_a = alarmSettings === null || alarmSettings === void 0 ? void 0 : alarmSettings.blockingSchedules) === null || _a === void 0 ? void 0 : _a.length)
                    ? alarmSettings.blockingSchedules.map((b) => ({
                        day: b.days !== undefined ? String(b.days) : "1",
                        from: parseTimeString(b.startTime),
                        to: parseTimeString(b.endTime),
                        fullDay: b.blockAlarmsForFullDay === true ||
                            (b.startTime === "00:00:00" && b.endTime === "23:59:59"),
                        // Preserve the stored timezone; fall back to browser timezone for legacy records.
                        timeZoneId: b.timeZoneId || BROWSER_TIMEZONE,
                    }))
                    : [],
            });
            setInitializedForNodeId(node.id);
        }
    }, [node, reset]);
    const addContact = () => {
        appendContact(defaultContact());
    };
    const handleRemoveContact = (index) => {
        if (contactFields.length > 1)
            removeContact(index);
    };
    const addBlockingSchedule = () => {
        appendSchedule({ day: "1", from: null, to: null, fullDay: false, timeZoneId: BROWSER_TIMEZONE });
    };
    const removeBlockingSchedule = (index) => {
        removeSchedule(index);
    };
    const handleAcknowledgeAlarms = () => {
        navigate("/alarms");
    };
    // Save alarm settings
    const handleSaveAlarmSettings = (data) => __awaiter(void 0, void 0, void 0, function* () {
        if (!(node === null || node === void 0 ? void 0 : node.id)) {
            toast.error(t("common:toasts.nodeIdRequired"));
            return;
        }
        const { recipients } = transformContactsForApi(data.contacts);
        try {
            const alarmSettingsPayload = {
                blockAllAlaramsAndSMS: data.blockAlarmEmails,
                alertType: parseAlertTypeOrDefault(data.alerting),
                alarmRepetition: parseRepeatAlarmOrDefault(data.repeatAlarm),
                recipients,
                blockingSchedules: data.blockingSchedules
                    .filter((b) => b.from || b.to || b.fullDay)
                    .map((b) => ({
                    days: parseInt(b.day),
                    blockAlarmsForFullDay: b.fullDay,
                    startTime: b.fullDay ? "00:00:00" : formatTimeToString(b.from),
                    endTime: b.fullDay ? "23:59:59" : formatTimeToString(b.to),
                    timeZoneId: b.timeZoneId || BROWSER_TIMEZONE,
                })),
            };
            const command = {
                id: node.id,
                alarmSettings: alarmSettingsPayload,
                isGeneralSettings: false,
                isAlarmSettings: true,
            };
            setJustSaved(true);
            yield dispatch(updateNode(command)).unwrap();
            toast.success(t("common:toasts.alarmSettingsUpdatedSuccessfully"));
        }
        catch (error) {
            toast.error((error === null || error === void 0 ? void 0 : error.message) || t("common:toasts.failedToSaveAlarmSettings"));
        }
    });
    const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("lg"));
    return (React.createElement(Grid, { container: true, spacing: 2 },
        React.createElement(Grid, { size: { xs: 12 } },
            React.createElement(Alert, { severity: "info", sx: {
                    backgroundColor: "#00B8D929",
                    border: "1px solid #00B8D929",
                    borderRadius: 1,
                    alignItems: "flex-start",
                    flexDirection: { xs: "column", sm: "row" },
                    "& .MuiAlert-icon": { color: "info.main" },
                    "& .MuiAlert-message": { flex: 1 },
                    "& .MuiAlert-action": {
                        alignItems: { xs: "flex-start", sm: "center" },
                        pt: 0,
                        pl: { xs: 0, sm: 1 },
                        ml: { xs: 0, sm: "auto" },
                        mt: { xs: 1.5, sm: 0 },
                        width: { xs: "100%", sm: "auto" },
                    },
                }, icon: isSmallScreen ? (false) : (React.createElement(Icon, { icon: "eva:alert-circle-fill", width: 24, height: 24 })), action: React.createElement(SButton, { variant: "contained", color: "info", size: isSmallScreen ? "small" : "medium", endIcon: React.createElement(Icon, { icon: "solar:arrow-right-up-linear" }), onClick: handleAcknowledgeAlarms, sx: { width: { xs: "100%", sm: "auto" } } }, t("common:actions.acknowledgeAlarms")) },
                React.createElement(Box, { sx: Object.assign(Object.assign({}, colCenter), { gap: 1 }) },
                    isSmallScreen && (React.createElement(Icon, { color: "#00B8D9", width: 20, height: 20, icon: "eva:alert-circle-fill" })),
                    React.createElement(Typography, { variant: "subtitle2", color: "info.dark", sx: { mb: 0.5 } }, t("common:banners.alarmRepetitionAlertTitle"))),
                React.createElement(Typography, { variant: "body2", color: "text.secondary" }, t("common:banners.alarmRepetitionAlertBody1")),
                React.createElement(Typography, { variant: "body2", color: "text.secondary", sx: { mt: 0.5 } }, t("common:banners.alarmRepetitionAlertBody2")))),
        React.createElement(Grid, { size: { xs: 12 } },
            React.createElement(SectionWithInfo, { title: t("common:headings.general"), tooltipText: generalTooltip, onTooltipSave: (value) => {
                    setGeneralTooltip(value);
                    handleTooltipSave(TOOLTIP_KEYS.alerting, value);
                } },
                React.createElement(Box, { sx: { mb: 2 } },
                    React.createElement(FSwitch, { color: "error", name: "blockAlarmEmails", control: alarmControl, label: t("common:labels.blockAlarmEmails") })),
                React.createElement(Grid, { container: true, spacing: 2 },
                    React.createElement(Grid, { size: { xs: 12, md: 6 } },
                        React.createElement(FSelect, { name: "alerting", control: alarmControl, label: t("common:labels.alarm"), options: alertingOptions })),
                    React.createElement(Grid, { size: { xs: 12, md: 6 } },
                        React.createElement(FSelect, { name: "repeatAlarm", control: alarmControl, label: t("common:labels.repeatAlarm"), options: repeatAlarmOptions }))))),
        React.createElement(Grid, { size: { xs: 12 } },
            React.createElement(SectionWithInfo, { title: t("common:headings.contactsList"), tooltipText: alarmsTooltip, onTooltipSave: (value) => {
                    setAlarmsTooltip(value);
                    handleTooltipSave(TOOLTIP_KEYS.alarmSms, value);
                } },
                React.createElement(Box, { sx: {
                        bgcolor: (theme) => theme.palette.background.paper,
                        borderRadius: 4,
                        px: 2,
                        py: 1,
                    } },
                    React.createElement(Typography, { variant: "h5", sx: { color: "primary.darker", mb: 2 } }, t("common:headings.contactsList")),
                    contactFields.map((field, index) => (React.createElement(Box, { key: field.id, mb: 1.5 },
                        React.createElement(Grid, { container: true, spacing: 2, alignItems: "center" },
                            React.createElement(Grid, { size: { xs: 12, sm: 6, md: 2.5 } },
                                React.createElement(FInput, { name: `contacts.${index}.email`, control: alarmControl, label: t("common:labels.emails"), placeholder: t("common:placeholders.emailAddress"), type: "email" })),
                            React.createElement(Grid, { size: { xs: 12, sm: 6, md: 2 } },
                                React.createElement(FPhoneInput, { name: `contacts.${index}.number`, control: alarmControl, label: t("common:labels.contactNumber", { number: index + 1 }) })),
                            React.createElement(Grid, { size: { xs: 12, sm: 6, md: 2.5 } },
                                React.createElement(FMultiSelectChips, { name: `contacts.${index}.alertReportTypes`, control: alarmControl, label: t("common:labels.alertReport"), options: nodeAlertReportOptions })),
                            React.createElement(Grid, { size: { xs: 12, sm: 3, md: "grow" } },
                                React.createElement(FTimePicker, { name: `contacts.${index}.startTime`, control: alarmControl, label: t("common:labels.startTimeOptional") })),
                            React.createElement(Grid, { size: { xs: 9, sm: 3, md: "grow" } },
                                React.createElement(FTimePicker, { name: `contacts.${index}.endTime`, control: alarmControl, label: t("common:labels.endTimeOptional") })),
                            React.createElement(Grid, { sx: {
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    width: "50px",
                                } },
                                React.createElement(SIconButton, { title: t("common:actions.remove"), size: "large", colorVariant: "inherit", sx: { borderRadius: "50px" }, onClick: () => handleRemoveContact(index), disabled: contactFields.length === 1 },
                                    React.createElement(Icon, { icon: "material-symbols:close-rounded" }))))))),
                    React.createElement(Box, { sx: Object.assign(Object.assign({}, jEnd), { mt: 1 }) },
                        React.createElement(SButton, { variant: "soft", color: "inherit", startIcon: React.createElement(Icon, { icon: "eva:plus-outline", width: 22, height: 22 }), onClick: addContact }, t("common:actions.addContact")))))),
        React.createElement(Grid, { size: { xs: 12 } },
            React.createElement(Box, { sx: {
                    bgcolor: (theme) => theme.palette.background.paper,
                    borderRadius: 4,
                    px: 2,
                    py: 1,
                } },
                React.createElement(Typography, { variant: "h5", sx: { color: "primary.darker", mb: 0.5 } }, t("common:headings.blockingSchedule")),
                React.createElement(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 0.5 } }, t("common:placeholders.blockingScheduleInfo")),
                React.createElement(Typography, { variant: "caption", color: "text.disabled", sx: { mb: 2, display: "block" } },
                    t("common:placeholders.browserTimezone"),
                    " ",
                    React.createElement("strong", null, BROWSER_TIMEZONE)),
                React.createElement(Grid, { container: true, spacing: 1 },
                    scheduleFields.map((field, index) => (React.createElement(React.Fragment, { key: field.id },
                        React.createElement(Grid, { size: { xs: 12, md: 2.5 } },
                            React.createElement(FSelect, { name: `blockingSchedules.${index}.day`, control: alarmControl, label: t("common:labels.day"), options: dayOptions })),
                        React.createElement(Grid, { size: { xs: 6, sm: 4, md: 3.5 } },
                            React.createElement(FTimePicker, { name: `blockingSchedules.${index}.from`, control: alarmControl, label: t("common:labels.from") })),
                        React.createElement(Grid, { size: { xs: 6, sm: 4, md: 3.5 } },
                            React.createElement(FTimePicker, { name: `blockingSchedules.${index}.to`, control: alarmControl, label: t("common:labels.to") })),
                        React.createElement(Grid, { size: { xs: 10, sm: 3, md: 1.7 }, sx: Object.assign({}, colCenter) },
                            React.createElement(FSwitch, { name: `blockingSchedules.${index}.fullDay`, control: alarmControl, color: "error", label: t("common:labels.blockForFullDay") })),
                        React.createElement(Grid, { size: { xs: 2, sm: 1, md: 0.8 }, sx: {
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            } },
                            React.createElement(SIconButton, { title: "Remove", size: "large", colorVariant: "inherit", sx: { borderRadius: "50px" }, onClick: () => removeBlockingSchedule(index) },
                                React.createElement(Icon, { icon: "material-symbols:close-rounded" })))))),
                    React.createElement(Grid, { size: { xs: 12 } },
                        React.createElement(Box, { sx: Object.assign({}, jEnd) },
                            React.createElement(SButton, { variant: "soft", color: "inherit", startIcon: React.createElement(Icon, { icon: "eva:plus-outline" }), onClick: addBlockingSchedule }, t("common:actions.addBlockingSchedule")))))))));
});
export default AlarmSettings;
